import { useI18N, useResponsive } from "@sundae/react-hooks";
import { Balance, TabsV3, Text } from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import { ADA_METADATA } from "@sundaeswap/core";
import { FC, useCallback, useMemo, useState } from "react";
import { joyrideSelectors } from "../../../constants/joyride.constants";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import TasteTestTVLChart from "../TasteTestTVLChart";
import TasteTestTargetPriceChart from "../TasteTestTargetPriceChart";
import TasteTestTransactionHistoryTable from "../TasteTestTransactionHistoryTable";

const {
  theme: { colors },
} = require("@sundae/tailwind-config");
const defaultPrimaryColor = colors.primary["DEFAULT"];

const TasteTestPriceChartTabView: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const [tabValue, setTabValue] = useState<"tvl" | "targetPrice" | "history">("targetPrice");
  const { xl } = useResponsive();
  const {
    state: { tasteTest, tasteTestPersonalization },
  } = useTasteTestContext();

  const adaAmountDeposited = useMemo(() => {
    const amount = tasteTest?.currentBalance?.tokenGathered.amount;
    return new AssetAmount(amount, ADA_METADATA);
  }, [tasteTest?.currentBalance?.tokenGathered.amount]);

  const handleTabChange = useCallback((value: string) => {
    setTabValue(value as "tvl" | "targetPrice");
  }, []);

  const getActiveTabState = useCallback(
    (tab: "tvl" | "targetPrice" | "history") => {
      if (tab === tabValue && !!tasteTestPersonalization?.primaryColor) {
        return "current";
      }

      if (tab === tabValue && !tasteTestPersonalization?.primaryColor) {
        return "info";
      }

      return "normal";
    },
    [tabValue, tasteTestPersonalization?.primaryColor]
  );

  return (
    <div data-guide-selector={joyrideSelectors.guides.tasteTest.interactiveGuide.priceChart}>
      <TabsV3
        withBorder={false}
        value={tabValue}
        size="lg"
        primitiveProps={{
          trigger: { className: "!pl-0", value: tabValue },
        }}
        onValueChange={handleTabChange}
        data={[
          {
            id: "targetPrice",
            content: <TasteTestTargetPriceChart />,
            trigger: (
              <div style={{ color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor }}>
                <Text tag="span" size="lg" variant={getActiveTabState("targetPrice")}>
                  {t("detail.priceCharts.tabs.price")}
                </Text>
              </div>
            ),
          },
          {
            id: "tvl",
            content: <TasteTestTVLChart />,
            trigger: (
              <div style={{ color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor }}>
                <Text
                  tag="span"
                  size="lg"
                  variant={
                    tabValue !== "tvl"
                      ? "normal"
                      : tasteTestPersonalization?.primaryColor
                      ? "current"
                      : "info"
                  }
                >
                  {t("detail.priceCharts.tabs.tvl")}
                  {xl && (
                    <>
                      {" "}
                      (
                      <Balance
                        abbreviate
                        containerClassName="!inline-flex"
                        locale={currentLocale}
                        showSymbol
                        size="lg"
                        value={adaAmountDeposited}
                        variant={getActiveTabState("tvl")}
                      />
                      )
                    </>
                  )}
                </Text>
              </div>
            ),
          },
          {
            id: "history",
            content: <TasteTestTransactionHistoryTable />,
            trigger: (
              <div style={{ color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor }}>
                <Text tag="span" size="lg" variant={getActiveTabState("history")}>
                  {t("detail.priceCharts.tabs.history")}
                </Text>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default TasteTestPriceChartTabView;
